<template>
  <div class="pages-slideshow-widget v-centerer">
    <div v-if="App.isEmbedded" class="text-center f-w" v-html="$t('message.SLIDESHOW_NESTED_EMBED')">
      <!-- App is embedded, probably in another slideshow widget. App should not be nested inside multiple slideshow widgets -->
    </div>
    <div v-else class="f-w f-h v-centerer">
      <div v-show="showControls" class="control-arrow" @click="prevStep">
        <img src="/images/prev.png" alt="">
      </div>
      <div class="component-holder f-h" :class="holderClasses">
        <div :id="`embedded-app${widget.id}`" />
        <div class="pages-slideshow-widget__interaction-blocker" :style="interactionBlockerStyle" />
      </div>
      <div v-show="showControls" class="control-arrow" @click="nextStep()">
        <img src="/images/next.png" alt="">
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Pages slideshow widget for configurable pages
 * Basically, this component creates a new app instance to display Shizen pages inside
 *
 * @vue-prop {Array} pages - Pages formatted as meeting steps (in settings/meetings)
 * @vue-prop {Boolean} showControls - Whether to display navigation arrows or not
 * @vue-prop {Boolean} autoScroll - Whether the pages should scroll automatically
 */

// Embedded App
import Vue from 'vue';
import Router from 'vue-router';
import routes from '@/routes';
import { i18n } from '@/plugins/i18n';
import { setRouterHooks } from '@/plugins/router';
import subapp from '@/App.vue';
import store from '@/store';

// Mixins
import mixinWidgets from '@/mixins/widgets.vue';
import mixinMeetingPages from '@/mixins/meeting-pages.vue';

export default {

  name: 'PagesSlideshowWidget',

  mixins: [mixinMeetingPages, mixinWidgets],

  props: {
    pages: {
      type: Array,
      default: () => [],
    },
    showControls: Boolean,
    autoScroll: {
      type: Boolean,
      default: true,
    },
    allowInteraction: Boolean,
  },

  data() {
    return {
      currentStepIndex: -1,
      nextStepTimeout: null,
      subapp: null,
    };
  },

  computed: {
    config() {
      return [
        {
          key: 'showControls',
          value: this.showControls,
          component: 'Checkbox',
          options: {
            title: this.$t('message.SLIDESHOW_CONTROLS'),
            class: 'block',
          },
        },
        {
          key: 'autoScroll',
          value: this.autoScroll,
          component: 'Checkbox',
          options: {
            title: this.$t('message.SLIDESHOW_AUTO_SCROLL'),
          },
        },
        {
          key: 'allowInteraction',
          value: this.allowInteraction,
          component: 'Checkbox',
          options: {
            title: this.$t('message.SLIDESHOW_ALLOW_INTERACTION'),
          },
        },
        {
          key: 'pagesTitle',
          value: '',
          component: 'Div',
          slots: [{
            component: 'Span',
            content: this.$t('message.PAGES'),
          }],
          options: {
            class: 'mt5 mb5 legend',
          },
        },
        {
          key: 'pages',
          value: this.pages,
          component: 'SortablePagePickers',
          options: {
            noMeetingPages: true,
          },
        },
      ];
    },
    currentStep() {
      if (this.currentStepIndex >= 0 && this.currentStepIndex < this.pages.length) {
        return this.pages[this.currentStepIndex];
      }
      return null;
    },
    /** Component wrapper classes */
    holderClasses() {
      return {
        'no-controls': !this.showControls,
      };
    },
    /** Object: Style of the interaction blocker element */
    interactionBlockerStyle() {
      return {
        display: this.allowInteraction ? 'none' : undefined,
      };
    },
  },

  watch: {
    pages() {
      this.restart();
    },
    editable(isEditable) {
      if (!isEditable) {
        this.restart();
      }
    },
  },

  created() {
    // Start slideshow
    if (this.pages && this.pages.length) {
      this.restart();
    }
  },

  mounted() {
    this.buildApp();
  },

  beforeDestroy() {
    clearTimeout(this.nextStepTimeout);
    if (this.subapp) {
      this.subapp.$destroy();
    }
  },

  methods: {
    /** Create a new app instance to display the components */
    buildApp() {
      if (this.appEmbedded) {
        return;
      }
      const router = new Router({ mode: 'abstract', routes });
      setRouterHooks(router);
      this.subapp = new Vue({
        i18n,
        router,
        store,
        render: h => h(subapp, { props: { isEmbedded: true } }),
      }).$mount(`#embedded-app${this.widget.id}`);
    },
    /** Cancel the timeout to start the screen saver and reset it */
    restart() {
      if (this.appEmbedded) {
        return;
      }
      clearTimeout(this.nextStepTimeout);
      this.currentStepIndex = -1;
      this.nextStep();
    },
    /** Automatically push the next step's page */
    nextStep(nStep = 1) {
      clearTimeout(this.nextStepTimeout);
      this.currentStepIndex += nStep + this.pages.length;
      this.currentStepIndex %= this.pages.length;
      // Wait nextTick for this.currentStep
      this.$nextTick(() => {
        // Timeout for next step
        if (!this.editable && this.autoScroll) {
          this.nextStepTimeout = setTimeout(() => {
            this.nextStep();
          }, this.currentStep.duration * 1000);
        }
        // Link with options (problems, pending, actions)
        const link = this.getPageLink(this.currentStep);
        if (this.subapp && this.subapp.$route.fullPath !== link) {
          this.subapp.$router.push(link);
        }
      });
    },
    /** Same as above but to go backward */
    prevStep() {
      this.nextStep(-1);
    },
  },
};
</script>

<style lang='scss'>
@import "@/assets/scss/vars.scss";

.pages-slideshow-widget {
  position: relative;

  .control-arrow {
    padding: 5px;
    border-radius: 3px;
    cursor: pointer;
    position: relative;
    z-index: 1;

    img {
      display: block;
      width: 15px;
    }
    &:hover {
      background-color: $color-hoverbtn-blue;
    }
  }
  .component-holder {
    width: calc(100% - 30px);
    position: relative;

    &.no-controls {
      width: 100%;
    }
  }
  #app {
    // Use transform to reset the global parent of fixed positioned elements
    transform: translateZ(0);
    overflow: hidden;
    background: $color-body-background;

    > #page-content {
      margin: 0px;
      left: 0px;
      width: 100%;
      background: none;

      .left-drawer-panel {
        left: 0px;

        &.right {
          left: auto;
          right: 0px;
        }
      }
    }

    .dark-overlay {
      top: 0px;
      left: 0px;
      height: 100% !important;
      width: 100% !important;
    }

    .file-viewer {
      left: 0px;
      width: 100%;
    }
  }

  &__interaction-blocker {
    background: transparent;
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    z-index: 10000;
  }
}
</style>
